import { useQueryClient } from "@tanstack/react-query"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Form, FormInput } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useFormState } from "@/hooks/use-form-state"
import {
  categoryFormSchema,
  createCategory,
  type TCategory,
  updateFinanceCategory,
} from "@/http/finance"

type TCategoryFormProps = {
  category?: TCategory
  organizationSlug: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  initialType?: "INCOME" | "EXPENSE"
}

export function CategoryForm({
  category,
  organizationSlug,
  open,
  onOpenChange,
  onSuccess,
  initialType,
}: TCategoryFormProps) {
  const isEditing = !!category
  const queryClient = useQueryClient()

  const { state, ...form } = useFormState({
    action: async (data) => {
      if (isEditing && category) {
        // Update existing category
        const { error } = await updateFinanceCategory({
          categoryId: category.id,
          name: data.name,
          organizationSlug,
        })

        if (error) {
          return {
            success: false,
            message: error.message || "Erro ao atualizar categoria",
          }
        }

        return {
          success: true,
          message: "Categoria atualizada com sucesso",
        }
      }
      // Create new category
      const payload = {
        name: data.name,
        type: data.type,
        organizationSlug,
      }

      const { error } = await createCategory(payload)

      if (error) {
        return {
          success: false,
          message: error.message || "Erro ao criar categoria",
        }
      }

      return {
        success: true,
        message: "Categoria criada com sucesso",
      }
    },
    zodSchema: categoryFormSchema,
    defaultValues: {
      name: category?.name || "",
      type: category?.type || initialType || "EXPENSE",
      organizationSlug,
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["finance-categories"],
        exact: false,
      })
      onSuccess?.()
      onOpenChange(false)
      form.reset()
    },
  })

  const handleCancel = () => {
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Categoria" : "Nova Categoria"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Edite as informações da categoria financeira"
              : "Crie uma nova categoria financeira para sua organização"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="grid gap-4 py-4">
            <FormInput name="name" label="Nome da Categoria">
              <Input placeholder="Ex: Vendas, Despesas..." />
            </FormInput>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Select
                value={form.watch("type")}
                onValueChange={(value) =>
                  form.setValue("type", value as "INCOME" | "EXPENSE")
                }
                disabled={isEditing}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INCOME">Receita</SelectItem>
                  <SelectItem value="EXPENSE">Despesa</SelectItem>
                </SelectContent>
              </Select>
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  O tipo de categoria não pode ser alterado
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={state.isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={state.isSubmitting}>
              {state.isSubmitting
                ? isEditing
                  ? "Atualizando..."
                  : "Criando..."
                : isEditing
                  ? "Atualizar"
                  : "Criar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
